import React, { useEffect, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";
import sh from "bundles/common/utils/sh";
import { SERIES } from "./chartTheme";
import OutputOverTimeChart from "./OutputOverTimeChart";
import ConversionFunnelChart from "./ConversionFunnelChart";
import OutcomeMixChart from "./OutcomeMixChart";
import BenchmarkChart from "./BenchmarkChart";
import CumulativeAreaChart from "./CumulativeAreaChart";
import WeekdayBarChart from "./WeekdayBarChart";
import ActivationRateChart from "./ActivationRateChart";
import CreatedActivatedChart from "./CreatedActivatedChart";

const ChartCard = ({ title, subtitle, children, height }) => (
  <Box sx={{ backgroundColor: "#ffffff", border: "1px solid hsl(140, 8%, 88%)", borderRadius: "8px", p: "16px", display: "flex", flexDirection: "column" }}>
    <Box sx={{ display: "flex", alignItems: "baseline", gap: "8px", mb: "12px" }}>
      <Typography sx={{ fontSize: 13, fontFamily: "Segoe UI Semibold, sans-serif, system-ui", color: "hsl(150, 30%, 12%)" }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontSize: 11, fontFamily: "Segoe UI Regular, sans-serif, system-ui", color: "hsl(150, 10%, 46%)" }}>
          {subtitle}
        </Typography>
      )}
    </Box>
    <Box sx={{ height, width: "100%" }}>{children}</Box>
  </Box>
);

const UserChartsSection = ({ userId, period, startDate = null, endDate = null, columns = 2, chartHeight = 220, extended = false }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userId) return;
    const params = { period };
    if (extended) params.extended = true;
    if (period === "custom" && startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    }
    setLoading(true);
    sh.get(`/internal/user_charts/${userId}`, { params })
      .then((res) => setData(res.data || null))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [userId, period, startDate, endDate, extended]);

  if (loading && !data) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={24} sx={{ color: "hsl(150, 38%, 17%)" }} />
      </Box>
    );
  }

  if (!data) return null;

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`, gap: "16px" }}>
      <ChartCard title="Output over time" subtitle="Last 8 weeks" height={chartHeight}>
        <OutputOverTimeChart points={data.output_over_time} />
      </ChartCard>
      <ChartCard title="Conversion funnel" subtitle="this period" height={chartHeight}>
        <ConversionFunnelChart funnel={data.funnel} />
      </ChartCard>
      <ChartCard title="Call outcomes" subtitle="this period" height={chartHeight}>
        <OutcomeMixChart data={data.outcome_mix} />
      </ChartCard>
      <ChartCard title="You vs team" subtitle="conversion rates" height={chartHeight}>
        <BenchmarkChart data={data.benchmark} />
      </ChartCard>

      {extended && data.period_series && (
        <>
          <ChartCard title="Candidates — cumulative" subtitle="this period" height={chartHeight}>
            <CumulativeAreaChart data={data.period_series} dataKey="cum_candidates" name="Candidates" color={SERIES.hired} />
          </ChartCard>
          <ChartCard title="Candidates by weekday" subtitle="Mon–Fri" height={chartHeight}>
            <WeekdayBarChart data={data.by_weekday} dataKey="candidates" name="Candidates" color={SERIES.hired} />
          </ChartCard>
          <ChartCard title="Created → activated" subtitle="with ≥1 application" height={chartHeight}>
            <CreatedActivatedChart created={data.created_vs_activated?.created} activated={data.created_vs_activated?.activated} />
          </ChartCard>
          <ChartCard title="Applications — activation rate" subtitle="apps per candidate" height={chartHeight}>
            <ActivationRateChart data={data.period_series} />
          </ChartCard>
          <ChartCard title="Applications — cumulative" subtitle="this period" height={chartHeight}>
            <CumulativeAreaChart data={data.period_series} dataKey="cum_applications" name="Applications" color={SERIES.applied} />
          </ChartCard>
          <ChartCard title="Applications by weekday" subtitle="Mon–Fri" height={chartHeight}>
            <WeekdayBarChart data={data.by_weekday} dataKey="applications" name="Applications" color={SERIES.applied} />
          </ChartCard>
        </>
      )}
    </Box>
  );
};

export default UserChartsSection;
